import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, X, Star, ArrowRight, Heart } from 'react-feather';
import { useNavigate } from 'react-router-dom';

// Import the SubscriptionHandler
import SubscriptionHandler from '../components/subscription/SubscriptionHandler';

interface PricingPageProps {
  isAuthenticated: boolean;
}

const PricingPage: React.FC<PricingPageProps> = ({ isAuthenticated = false }) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const navigate = useNavigate();
  const pricingRef = useRef<HTMLDivElement>(null);
  const pricingInView = useInView(pricingRef, { once: false, amount: 0.3 });
  
  const handleRegisterClick = (plan: string) => {
    navigate(`/register?plan=${plan}`);
  };

  // Pricing plans - defined with both monthly and yearly options
  const plans = [
    {
      name: 'Basic',
      planType: 'basic' as const,
      description: 'Perfect for small, intimate weddings',
      monthly: 0,
      yearly: 0,
      features: [
        { included: true, text: 'Wedding Website' },
        { included: true, text: 'Guest List Management' },
        { included: true, text: 'Basic Gift Registry' },
        { included: true, text: 'RSVP Management' },
        { included: true, text: 'Limited to 5 Images' },
        { included: true, text: 'Cash Collection (with fees)' },
        { included: false, text: 'Custom Domain' },
        { included: false, text: 'Priority Support' },
      ],
      cta: 'Start for Free',
      color: 'from-gray-500 to-gray-600',
      popular: false,
    },
    {
      name: 'Premium',
      planType: 'premium' as const,
      description: 'For couples who want more features',
      monthly: 9.99,
      yearly: 7.99,
      features: [
        { included: true, text: 'Everything in Basic' },
        { included: true, text: 'Custom Domain' },
        { included: true, text: 'Unlimited Images' },
        { included: true, text: 'Lower Withdrawal Fees' },
        { included: true, text: 'Advanced Todo List' },
        { included: true, text: 'Photo Gallery' },
        { included: false, text: 'Priority Support' },
        { included: false, text: 'Multi-currency Support' },
      ],
      cta: 'Choose Premium',
      color: 'from-[#FF6B6B] to-[#F9A8D4]',
      popular: true,
    },
    {
      name: 'Ultimate',
      planType: 'ultimate' as const,
      description: 'The complete wedding planning experience',
      monthly: 20,
      yearly: 16,
      features: [
        { included: true, text: 'Everything in Premium' },
        { included: true, text: 'Priority Support' },
        { included: true, text: 'Multi-currency Support (₦ & $)' },
        { included: true, text: 'Lowest Withdrawal Fees' },
        { included: true, text: 'Vendor Management' },
        { included: true, text: 'VIP Payment Processing' },
        { included: true, text: 'Budget Management' },
        { included: true, text: 'Invitation Management' },
      ],
      cta: 'Choose Ultimate',
      color: 'from-[#2ECC71] to-[#FFD700]',
      popular: false,
    },
  ];

  // Calculate savings
  const getYearlySavings = (plan: typeof plans[0]) => {
    const monthlyCost = plan.monthly * 12;
    const yearlyCost = plan.yearly * 12;
    const savings = monthlyCost - yearlyCost;
    const savingsPercent = Math.round((savings / monthlyCost) * 100);
    return { savings, savingsPercent };
  };

  return (
    <div className="bg-gradient-to-b from-white to-[#FFF8E7] min-h-screen pt-24 pb-16 font-inter">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Pricing Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block mb-3 px-4 py-1 rounded-full bg-[#F9A8D4]/20 text-[#FF6B6B] text-sm font-medium">
            Pricing Plans
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-['Playfair_Display'] text-gray-900 mb-6">
            Choose the Perfect Plan for Your Perfect Day
          </h1>
          <motion.div 
            className="h-1 w-24 bg-[#FF6B6B] mx-auto mb-6"
            initial={{ width: 0 }}
            animate={pricingInView ? { width: 96 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          />
          <p className="text-xl text-gray-600 mb-10">
            Select a plan that fits your wedding needs, from intimate gatherings to grand celebrations.
          </p>

          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-full inline-flex">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 text-sm font-medium rounded-full transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white shadow-md text-gray-900'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 text-sm font-medium rounded-full transition-all ${
                  billingPeriod === 'yearly'
                    ? 'bg-white shadow-md text-gray-900'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="ml-1 bg-[#FF6B6B] text-white text-xs px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div ref={pricingRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => {
            const { savings, savingsPercent } = getYearlySavings(plan);
            return (
              <motion.div
                key={plan.name}
                className={`relative rounded-2xl bg-white shadow-xl overflow-hidden border border-gray-100 ${
                  plan.popular ? 'md:-mt-8 md:mb-8' : ''
                }`}
                initial={{ opacity: 0, y: 50 }}
                animate={pricingInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#FF6B6B] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}

                {/* Plan Header */}
                <div className={`p-8 bg-gradient-to-r ${plan.color} text-white`}>
                  <h3 className="text-2xl font-bold mb-1">{plan.name}</h3>
                  <p className="opacity-90 mb-6">{plan.description}</p>
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold">
                      {plan.monthly === 0 && billingPeriod === 'monthly' && 'Free'}
                      {plan.yearly === 0 && billingPeriod === 'yearly' && 'Free'}
                      {(billingPeriod === 'monthly' && plan.monthly > 0) &&
                        `$${plan.monthly}`}
                      {(billingPeriod === 'yearly' && plan.yearly > 0) &&
                        `$${plan.yearly}`}
                    </span>
                    {plan.monthly > 0 && (
                      <span className="ml-2 text-lg opacity-90">
                        /{billingPeriod === 'monthly' ? 'month' : 'month, billed yearly'}
                      </span>
                    )}
                  </div>
                  
                  {/* Yearly Savings */}
                  {billingPeriod === 'yearly' && plan.monthly > 0 && (
                    <div className="mt-2 text-sm bg-white/20 rounded-full px-3 py-1 inline-block">
                      Save ${savings.toFixed(2)} per year (20% off)
                    </div>
                  )}
                </div>

                {/* Features List */}
                <div className="p-8">
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-[#2ECC71] mr-3 flex-shrink-0" />
                        ) : (
                          <X className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                        )}
                        <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Replace CTA Button with SubscriptionHandler */}
                  <SubscriptionHandler 
                    isAuthenticated={isAuthenticated}
                    planType={plan.planType}
                    planDuration={billingPeriod}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-['Playfair_Display'] text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">Find answers to common questions about our pricing plans.</p>
          </div>

          <div className="space-y-6">
            <FaqItem
              question="What happens when my wedding is over?"
              answer="Your wedding website will remain active for a full year after your wedding date. You can download and save all your wedding data, photos, and memories before the subscription ends."
            />
            <FaqItem
              question="Can I upgrade my plan later?"
              answer="Absolutely! You can upgrade your plan at any time. When you upgrade, you'll only pay the prorated difference for the remainder of your billing period."
            />
            <FaqItem
              question="Is there a limit to the number of images I can upload?"
              answer="Yes. The Basic plan limits you to 5 images for your wedding site. Premium and Ultimate plans offer unlimited image uploads for a more comprehensive wedding website experience."
            />
            <FaqItem
              question="How does the payment processing work for gift registry?"
              answer="We integrate with Paystack for secure payment processing. Different withdrawal fees apply based on your plan - Basic plans have higher fees, while Premium and Ultimate plans enjoy progressively lower fees for withdrawals. All monetary gifts can be collected in either Naira or US Dollars."
            />
            <FaqItem
              question="Do you offer refunds?"
              answer="Yes, we offer a 14-day money-back guarantee. If you're not satisfied with our service, contact us within 14 days of purchase for a full refund."
            />
          </div>
        </div>

        {/* Testimonial Banner */}
        <div className="mt-24 bg-gradient-to-r from-[#FF6B6B]/10 to-[#2ECC71]/10 rounded-2xl p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-xl italic text-gray-700 mb-6">
                "TaleOfTwo made planning our Owambe so effortless! The Premium plan was worth every penny for our 300-guest celebration. The wedding fund feature helped us collect contributions from family abroad."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-[#F9A8D4]/20 mr-4 flex items-center justify-center text-[#FF6B6B]">
                  <Heart className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Chioma & David</p>
                  <p className="text-sm text-gray-600">Lagos, Nigeria</p>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 lg:pl-12">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full"
              >
                <SubscriptionHandler
                  isAuthenticated={isAuthenticated}
                  planType="premium"
                  planDuration={billingPeriod}
                />
              </motion.div>
              <p className="text-center mt-4 text-gray-600">
                Join thousands of happy couples planning their dream weddings
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// FAQ Component
const FaqItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        className="w-full px-6 py-4 text-left font-medium flex justify-between items-center hover:bg-gray-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-gray-900">{question}</span>
        <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="px-6 py-4 bg-gray-50"
        >
          <p className="text-gray-600">{answer}</p>
        </motion.div>
      )}
    </div>
  );
};

export default PricingPage;